import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Button,
  Card,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Box from "@mui/material/Box";
import SearchBar from "../../components/SearchBar";
import { useNavigate } from "react-router-dom";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { isValidInput } from "../../utils/validations";
import { CommonBody } from "../../types/General";
import { showError, showToast, Loader } from "../../constants";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import SkeletonTable from "../../components/SkeletonTable";
import WarnModal from "../../components/WarnModal";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/Translation";
import {
  useDeleteDiscountMutation,
  useLazyGetAllDiscountQuery,
  useUpdateDiscountMutation,
} from "../../services/discount";
import moment from "moment";
import Pagination from "../../components/Pagination";
import NoDataFound from "../../components/NoDataFound";

const ManageDiscount = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  console.log(userData);
  const [getDiscountsQuery, { isLoading, isFetching, isSuccess }] = useLazyGetAllDiscountQuery();
  const [updateDiscount] = useUpdateDiscountMutation();
  const [deleteDiscount] = useDeleteDiscountMutation();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [discountData, setDiscountData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [allowStatusToggle, setAllowStatusToggle] = useState(false);

  const [hidePermission, setHidePermission] = useState<any>({
    isAdd: true,
    isDelete: true,
  });
  console.log(hidePermission, "hidePermission");
  const translate = useTranslation();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };
  let totalPages = Math.ceil(totalCount / 10);

  const getDiscounts = async () => {
    setLoading(true);
    try {
      const response = await getDiscountsQuery({
        query: debouncedSearchTerm.trim(),
        page: page,
      }).unwrap();
      if (response?.statusCode === 200) {
        setDiscountData(response?.data?.data);
        setTotalCount(response?.data?.total);
      } else {
        setDiscountData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteDiscount({ id: id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(translate?.manageDiscount?.promoCodeDeleted || "Promo Code deleted successfully");
        getDiscounts();
      }
    } catch (error: any) {
      console.log(error, "errror");
      showError(error?.data?.message || "");
    }
  };

  const updateUserStatus = async (id: string, status: boolean) => {
    try {
      const encryptedBody = generateEncryptedKeyBody({
        isBlocked: status,
      }) as CommonBody;

      const response = await updateDiscount({
        id,
        encryptedBody,
      }).unwrap();

      if (response?.statusCode === 200) {
        const updatedData = discountData?.map((x: any) => {
          if (x._id === id) {
            return {
              ...x,
              isBlocked: status,
            };
          }
          return x;
        });
        setDiscountData(updatedData);
        showToast(translate?.manageDiscount?.statusUpdated || "Status updated successfully");
      } else {
        showError(translate?.manageDiscount?.pleaseTryAgain || "Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.manageDiscount?.pleaseTryAgain || "Please try again");
    }
  };

  const formatNumberWithCommas = (number: any) => {
    if (number > 0) {
      const formattedNumber = number.toLocaleString("en-IN");
      return `${formattedNumber}`;
    }
    return "";
  };

  const label = { inputProps: { "aria-label": "Switch demo" } };

  const checkPermission = () => {
    const permissions = userData?.permission;
    if (permissions?.length) {
      let idx = -1;
      idx = permissions?.findIndex(
        (ele: any) => ele?.name === "Manage Promotional Code"
      );

      if (idx != -1) {
        let data = {
          isAdd: permissions[idx]?.isAdd,
          isDelete: permissions[idx]?.isDelete,
        };
        setHidePermission(data);
      } else {
        setHidePermission({ isAdd: false, isDelete: false });
      }
    }
  };

  useEffect(() => {
    if (userData && userData?.permission?.length) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    getDiscounts();
  }, [page, debouncedSearchTerm]);

  useEffect(() => {
    setAllowStatusToggle(false);
    const timer = window.setTimeout(() => setAllowStatusToggle(true), 300);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} isLinear />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng"> {translate?.manageDiscount?.managePromotionalCode || "Manage Promotional Code"}</h1>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
            <Box className="cards_header_right">
              {hidePermission?.isAdd ? (
                <Button
                  className="btn btn_primary"
                  onClick={() => navigate("/manage-discount/add")}
                >
                  {translate?.manageDiscount?.addPromotionalCode || "Add Promotional Code"}
                </Button>
              ) : null}
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Box className="heading"></Box>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center">{translate?.main?.SNo}</TableCell>
                  <TableCell>{translate?.manageDiscount?.discountCode || "Discount Code"}</TableCell>
                  <TableCell>{translate?.manageDiscount?.discountName || "Discount Name"} (EN)</TableCell>
                  <TableCell>{translate?.manageDiscount?.discountName || "Discount Name"} (AR)</TableCell>
                  <TableCell>{translate?.manageDiscount?.discountType || "Discount Type"}</TableCell>
                  <TableCell>{translate?.manageDiscount?.userType || "User Type"}</TableCell>
                  <TableCell>{translate?.manageDiscount?.discountValue || "Discount Value"}</TableCell>
                  <TableCell>{translate?.manageDiscount?.minAmountToApply || "Min Amount To Apply"}</TableCell>
                  <TableCell>{translate?.manageDiscount?.startDate || "Start Date"}</TableCell>
                  <TableCell>{translate?.manageDiscount?.endDate || "End Date"}</TableCell>
                  {hidePermission?.isAdd ? <TableCell>{translate?.manageDiscount?.status || "Status"}</TableCell> : null}
                  {(hidePermission?.isAdd || hidePermission?.isDelete) ? <TableCell>{translate?.main?.Action}</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTable columns={12} rows={10} />
                ) : discountData?.length > 0 ? (
                  discountData?.map((row: any, i: any) => (
                    <TableRow key={row?._id}>
                      <TableCell align="center">{(page - 1) * 10 + i + 1}</TableCell>
                      <TableCell>{row?.discountCode || "-"}</TableCell>
                      <TableCell>{row?.discountName || "-"}</TableCell>
                      <TableCell>{row?.discountName_ar || "-"}</TableCell>
                      <TableCell>
                        {row?.discountType === 1 ? translate?.manageDiscount?.percentage || "Percentage" : translate?.manageDiscount?.flat || "Flat"}
                      </TableCell>
                      <TableCell>
                        {row?.userType === 1
                          ? translate?.dashboard?.User || "User"
                          : row?.userType === 2
                            ? translate?.main?.Agent || "Agent"
                            : row?.userType === 3
                              ? translate?.manageDiscount?.both || "Both"
                              : "-"}
                      </TableCell>
                      <TableCell>{row?.discountValue || "-"}</TableCell>
                      <TableCell>{row?.minAmountToApply || "-"}</TableCell>
                      <TableCell>
                        {moment(row?.startDate).format("MM-D-YY")}
                      </TableCell>
                      <TableCell>
                        {moment(row?.endDate).format("MM-D-YY")}
                      </TableCell>

                      {hidePermission?.isAdd ? (
                        <TableCell>
                          <Switch
                            {...label}
                            size="small"
                            disabled={!allowStatusToggle}
                            checked={!row?.isBlocked}
                            onChange={(event) => {
                              if (!allowStatusToggle) return;
                              updateUserStatus(row?._id, !event.target.checked);
                            }}
                          />
                        </TableCell>
                      ) : null}
                      {(hidePermission?.isAdd || hidePermission?.isDelete) ? (
                        <TableCell>
                          <Box className="table_actions">
                            {hidePermission?.isAdd ? (
                              <IconButton
                                onClick={() =>
                                  navigate(`/manage-discount/edit/${row?._id}`)
                                }
                              >
                                <ModeEditIcon />
                              </IconButton>
                            ) : null}
                            {hidePermission?.isDelete ? (
                              <IconButton
                                onClick={() => {
                                  setOpen(true);
                                  setSelectedId(row?._id);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : null}
                          </Box>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {translate?.manageDiscount?.noPromotionalCodeFound || "No Promotional Code Found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {discountData?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={discountData}
            page={page}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        ) : (
          ""
        )}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => handleDelete(selectedId)}
        name="Code"
      />
    </MainContainer>
  );
};

export default ManageDiscount;
